/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'

import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../shared/modal'
import { SecondaryButton } from '../../shared/buttons'
import { ModalSize } from '../../shared/modal/Modal/Modal'

type TermsOfServiceModalProps = {
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onCancel?: () => void
}

const defaultProps: Pick<TermsOfServiceModalProps, 'onCancel'> = {
  onCancel: undefined
}

const textCss = css`
  height: 500px;
  overflow: scroll;

  ol {
    padding: 0;
    list-style-position: inside;

    & > li > span:first-of-type {
      font-weight: 500;
      padding-left: 24px;
    }

    ol {
      padding: 10px 24px;
    }
  }
`
const headingCss = css`
  text-align: center;
  text-decoration: underline;
`
const lastUpdatedCss = css`
  text-align: center;
`
const upperCss = css`
  text-transform: uppercase;
`

const TermsOfServiceModal = ({ isOpen, onCancel }: TermsOfServiceModalProps) => (
  <Modal isOpen={isOpen} size={ModalSize.LG}>
    <ModalHeader onClose={onCancel} title="Terms of Service" />
    <ModalBody>
      <div css={textCss}>
        <p css={headingCss}>
          <strong>Civitas Application User Terms</strong>
        </p>

        <p css={lastUpdatedCss}>Last Updated: August 15, 2020</p>

        <p>
          PLEASE READ: THESE USER TERMS CONTAIN A MANDATORY ARBITRATION AGREEMENT. PLEASE REVIEW THE
          ARBITRATION AGREEMENT CAREFULLY AS IT MAY AFFECT YOUR LEGAL RIGHTS.
        </p>

        <p>
          These terms and conditions (&quot;<strong>User Terms</strong>&quot;) are a legal contract
          between you and Civitas Learning, Inc. (together with its affiliates, &quot;
          <strong>Civitas</strong>
          &quot;) for the access to and use of any of Civitas’ Software as a Service Applications
          (the &quot;<strong>Application(s)</strong>&quot;), which include the website, software,
          mobile application, product information, training programs, documentation, messages
          generated by the software, and any other content. The Applications may include, but are
          not limited to: Inspire, ClearScholar, and, College Scheduler. As part of these User
          Terms, you agree to comply with the most recent versions of our Acceptable Use Policy and
          Privacy Policy, which are incorporated by reference into these User Terms. Other Civitas
          products and services, including the Civitas website and webinars, are governed by other
          terms and conditions.
        </p>

        <p>
          For the purposes of these User Terms, &quot;affiliates&quot; means any entity that
          directly or indirectly controls, is controlled by, or is under common control with
          Civitas, whereby &quot;control&quot; (including, with correlative meaning, the terms
          &quot;controlled by&quot; and &quot;under common control&quot;) means the possession,
          directly or indirectly, of the power to direct, or cause the direction of the management
          and policies of such person, whether through ownership of voting securities, by contract,
          or otherwise.
        </p>

        <p>
          Civitas has separately entered into a written agreement (&quot;
          <strong>Customer Contract</strong>&quot;) with an educational institution (herein referred
          to as &quot;<strong>Customer</strong>&quot; that provided Customer with a subscription to
          access and configure an instance of one or more Civitas Applications. This Customer
          Contract allows Customer to designate users of its instance(s) of the Application(s). Each
          user invited to access the Application(s) by Customer, including you, is an &quot;
          <strong>Authorized User</strong>.&quot; If you are accessing or using the Application(s)
          as part of your employment with or as an independent contractor of Customer, these User
          Terms may be superseded by the Customer Contract. For all other Authorized Users, these
          User Terms shall apply. If you are unsure whether these User Terms or the Customer
          Contract apply to your use of the Application(s), please contact Customer. To the extent
          there is a conflict between the Customer Contract and these User Terms, the Customer
          Contract shall apply.
        </p>

        <p>
          By accessing or using the Application(s), you hereby certify that you are an Authorized
          User of a Civitas Customer and are accessing or using the Application(s) for an authorized
          purpose only. You further certify that you are at least 18 years of age and competent to
          agree to these User Terms, and that you reside in the United States or any of its
          territories or possessions. By accessing or using THE APPLICATION(S), you agree to be
          bound by these User Terms, including the warranty disclaimers, indemnity and limitation of
          liability, AND ARBITRATION provisions below and the privacy policy and acceptable use
          policy. if you do not understand or agree to these User terms, do not access or use THE
          APPLICATION(S) and please exit THE APPLICATION(S) now.
        </p>

        <p css={upperCss}>
          THE APPLICATION(S), you agree to be bound by these User Terms, including the warranty
          disclaimers, indemnity and limitation of liability, AND ARBITRATION provisions below and
          the privacy policy and acceptable use policy. if you do not understand or agree to these
          User terms, do not access or use THE APPLICATION(S) and please exit THE APPLICATION(S)
          now.
        </p>

        <p>
          These User Terms including the Acceptable Use Policy and Privacy Policy may be changed
          from time to time, please check these User Terms and the Acceptable Use Policy and Privacy
          Policy regularly to stay informed of any changes to the User Terms. By continuing to
          access or use the Application(s) following a change in User Terms, you thereby agree to be
          bound by those terms. If you do not agree with a change in the User Terms, please
          discontinue your access to or use of the Application(s).
        </p>

        <ol type="I">
          <li>
            <span>Authorized Use</span>
            <p>
              As an Authorized User, Civitas authorizes you to access and use the Application(s) and
              the Materials (defined below) only for personal educational, non-commercial use,
              provided that you comply with these User Terms, the Privacy Policy, and the Acceptable
              Use Policy. You may not access or use the Application(s) while located outside of the
              United States or any of its territories or possessions. Civitas may revoke your right
              to access and use the Application(s) or the Materials at any time and for any reason
              within its sole discretion. Your right to access and use the Application(s) will
              terminate at Civitas’ discretion, at Customer’s discretion (by revoking your User
              Account) or upon your failure to abide by these User Terms.
            </p>
            <p>
              You are authorized to access and/or use the Application(s) only through the log-in
              credentials supplied to you by Customer (&quot;<strong>User Account</strong>&quot;)
              and only through the application interface provided by Civitas. If Customer revokes
              your User Account for any reason, you are no longer authorized to access and/or use
              the applicable Application(s). Your log-in credentials and User Account details may
              not be shared with or used by anyone other than you to access and/or use the
              Application(s). It is your responsibility to safeguard your login and password
              credentials and immediately notify Civitas of any unauthorized use or breach of
              security. You are solely responsible for any authorized or unauthorized access to your
              account by any person. You agree to bear all responsibility for the confidentiality of
              your password and all use, purchases, or charges incurred from use of the
              Application(s) with your User Account. Civitas has no liability or responsibility for
              any use of your User Account caused by unauthorized access to or fraudulent attacks on
              the Application(s) or Civitas generally.
            </p>

            <p>
              When using the Application(s), you are prohibited from (a) using or attempting use to
              spiders, robots, avatars, intelligent agents, or any other extraction, navigation, or
              search tools, except for a normal browser as used by a natural persons, (b)
              aggregating, copying, or duplicating any of the materials or information available
              from the Application(s), except for the small amount of materials and information
              temporarily required for an ordinary single use of the Application(s), (c) accessing
              data not intended for you or attempting to gain unauthorized access to, interfere
              with, or disrupt any parts of the Application(s), the server on which the
              Application(s) is stored, or any server, computer or database connected to the
              Application(s), (d) accessing the Application(s) for the purpose of competing with
              Civitas or using the Application(s) for the purposes of benchmarking or other similar
              purposes, (e) using the Application(s) in any manner that could disable, overburden,
              damage, or impair the site or interfere with any other party’s use of the
              Application(s), or (f) attacking the Application(s) or Civitas generally via a
              denial-of-service attack or a distributed denial-of-service attack.
            </p>
            <p>
              For the avoidance of doubt, access to the Application(s) does not give you any
              ownership right to any part of that Application or any Civitas Application. The
              Application(s) are the sole property of Civitas.
            </p>
          </li>
          <li>
            <span>Your Content & Personal Information</span>
            <p>
              You may decide to submit content or information to the Application(s) by sending
              messages, inputting information, making selections, or uploading data or files (&quot;
              <strong>Content</strong>.&quot;) Some of the Content you choose to enter may be
              personally identifiable information or data from education records that may be subject
              to the Family Education Rights and Privacy Act of 1974, 20 U.S.C. §1232g (FERPA) or
              other relevant data privacy laws. In addition to the data and information you provide,
              Customer may provide the Application(s) with access to your educational records from
              Customer’s student information system, learning management system, or similar records
              databases (&quot;<strong>Customer Records</strong>&quot;). You acknowledge and agree
              that the Customer Records, your Content and any content input by Customer or its
              employees or agents, is owned by Customer and is governed by the Customer Contract
              (&quot;<strong>Customer Data</strong>&quot;).
            </p>
            <p css={upperCss}>
              As between Civitas and Customer, you agree that it is solely Customer’s responsibility
              to (i) inform you of any relevant Customer policies and practices and any settings
              that may impact the processing of Customer Data; (ii) obtain any rights, permissions
              or consents from you that are necessary for the lawful use of Customer Data and the
              operation of THE APPLICATION(S); (iii) ensure that the transfer and processing of
              Customer Data under the Customer Contract is lawful; and (iv) respond to and resolve
              any dispute with you relating to or based on Customer Data, THE APPLICATION(S), or
              Customer’s failure to fulfill these obligations.
            </p>
            <p>
              Customer may (or may instruct Civitas to) provision or deprovision access to the
              Application(s), enable or disable certain functionalities, manage permissions, export
              or download data, change settings, share information, or delete or destroy data at its
              discretion. These choices or instructions may result in loss of access, loss of use,
              disclosure, modification, or deletion of certain or all Customer Data including your
              Content. You acknowledge that you bear sole responsibility for adequate security,
              protection, and backup of your Content.{' '}
              <span css={upperCss}>
                Civitas will have no liability to you for any instructions given by Customer related
                to the disposition, modification, or access to Customer Data or any unauthorized
                access or use, corruption, deletion, destruction of loss of any Customer Data.
              </span>
            </p>
            <p>
              Please review our Privacy Policy for more information on how we collect and use
              Customer Data and data relating to the use and performance of the Application(s).
            </p>
          </li>
          <li>
            <span> Accuracy of Information</span>
            <p>
              The information presented on or through the Application(s) is made available solely
              for general information purposes. Civitas does not warrant the accuracy, completeness
              or usefulness of this information. Any reliance you place on such information is
              strictly at your own risk. Civitas disclaims all liability and responsibility arising
              from any reliance placed on such materials by you or any other user of the
              Application(s), or by anyone who may be informed of any of the content on the
              Application(s).
            </p>
          </li>
          <li>
            <span>Intellectual Property</span>
            <p>
              You acknowledge that the Applications, and Civitas’ designs, trademarks, service
              marks, logos (the &quot;<strong>Marks</strong>&quot;), graphics, text, sounds,
              pictures, and other files and the selection, and arrangement thereof, (collectively
              the &quot;<strong>Materials</strong>&quot;) are the property of Civitas and are
              subject to and protected by applicable intellectual property laws and rights. All
              rights to the Application(s) and the Materials not expressly granted in these User
              Terms are reserved to their respective copyright owners. Any use of the Application(s)
              or the Materials must keep intact all copyright and other proprietary notices
              contained in the original Materials. Except as expressly authorized by these User
              Terms, you may not copy, modify, reproduce, distribute, republish, download, perform,
              display, post, transmit, exploit, create derivative works or otherwise use the
              Application(s) or any of the Materials in any form or by any means without prior
              written authorization of Civitas. Civitas Marks contained or described in the
              Application(s) or any of the Materials are the sole property of Civitas and may not be
              copied, altered or otherwise used, in whole or in part, without the prior written
              authorization of Civitas. Civitas reserves the right to enforce its intellectual
              property rights to the fullest extent of the law.
            </p>
            <p>
              You may not support or allow any third party to access, copy, distribute, sell,
              disclose, lend, transfer, convey, modify, decompile, disassemble, or reverse engineer
              any Application(s) or the Materials for any purpose whatsoever.
            </p>
          </li>
          <li>
            <span>Feedback</span>
            <p>
              You may provide suggestions, enhancement requests, recommendations, or other feedback
              to Civitas concerning the functionality and performance of the Application(s) (&quot;
              <strong>Feedback</strong>&quot;) from time to time. You hereby assign and agree to
              assign all of your right, title, and interest in and to such Feedback to Civitas. To
              the extent that the foregoing assignment is ineffective for whatever reason, you
              hereby grant and agree to grant to Civitas a non-exclusive, perpetual, irrevocable,
              royalty free, worldwide right and license to use, reproduce, disclose, sublicense,
              distribute, modify and otherwise exploit such Feedback without restriction.
            </p>
          </li>
          <li>
            <span>Term</span>
            <p>
              Your authorization to access and use the Application(s) will commence upon your
              acceptance of these User Terms and will remain in effect until Customer’s subscription
              for you expires or terminates, or your access to the Application(s) has been
              terminated by Customer or Civitas. Civitas may suspend your access to an Application
              for any reason or for no reason at our discretion without prior advance notice. Please
              contact Customer if you at any time or for any reason wish to terminate your User
              Account. Upon termination of these User Terms all of your rights under these User
              Terms shall immediately terminate.
            </p>
            <p>
              Civitas may suspend your access to the Application(s) or terminate these User Terms
              for cause if: (i) you violate any provision of these User Terms or Civitas has reason
              to believe you have violated these User Terms, the Privacy Policy, or the Authorized
              Use Policy; (ii) there is an unusual spike or increase in your use of an Application
              for which there is reason to believe such traffic or activity is fraudulent, abusive,
              harmful, threatening, defamatory, offensive, and/or negatively impacting the operating
              capability of an Application or Civitas generally; (iii) Civitas determines, in our
              sole discretion, that allowing you to access and/or use an Application is prohibited
              by applicable law, or has become impractical or unfeasible for any legal or regulatory
              reason; (iv) upon request from the Customer who invited you to use the Application(s);
              or (v) due to an uncured breach and/or termination of the Customer Agreement.
            </p>
            <p>
              Notwithstanding such termination or suspension, you will continue to be subject to the
              &quot;Intellectual Property,&quot; &quot;Feedback,&quot; &quot;Indemnification and
              Limitation of Liability,&quot; &quot;Arbitration,&quot; and &quot;Applicable Law &
              Jurisdiction&quot; sections of these User Terms. You agree that Civitas shall not be
              liable to you or to any third party for any modification, suspension, termination, or
              discontinuation of an Application or your access to/ability to use an Application.
            </p>
          </li>
          <li>
            <span>Downtime</span>
            <p>
              Your access to and/or use of the Application(s) may be suspended for the duration of
              any unanticipated, unscheduled, or scheduled downtime or unavailability of any portion
              or all of the Application(s) for any reason, including as a result of power outages,
              system failures, in the event of a denial of service attack or other attack on the
              Application(s) or other event that we determine, in our sole discretion, may create a
              risk to the Application(s), to you or to Customer or any of our other users or
              customers if not suspended, or other interruptions. Civitas shall have no liability
              whatsoever for any damage, liabilities, losses (including any loss of data or profits)
              or any other consequences that you may incur as a result of any such suspension of
              service.
            </p>
          </li>
          <li>
            <span>Third-Party Sites</span>
            <p>
              Any links to Customer or third-party websites within the Application(s) are provided
              only as a convenience to you. If you use these links, you will leave the
              Application(s). Civitas does not control or endorse any third-party websites. You
              agree that Civitas will not be responsible or liable for any content, goods or
              services provided on or through these outside websites or for your use or inability to
              use such links or websites. You will use these links at your own risk. You are advised
              that other websites, including third-party websites linked from the Application(s) may
              contain material or information that you may find offensive or inappropriate; that is
              inaccurate, untrue, misleading or deceptive; or that is defamatory, libelous,
              infringing of others’ rights or otherwise unlawful. You should carefully review any
              third-party&apos;s policies and practices to make sure you understand them before you
              access that third-party’s website or engage in any transaction with any third-party.
              Any complaints, claims, concerns, or questions regarding third-party websites,
              products, or services should be directed to the third-party. Civitas expressly
              disclaims any responsibility for the content, legality, decency or accuracy of any
              information, and for any products and services that appear on any third-party website.
            </p>
          </li>
          <li>
            <span>Notifications & Messaging</span>
            <p>
              You may have the option to receive email and/or SMS notifications and messages related
              to your use of the Application(s) including, but not limited to, appointment
              notifications and reminders, messaging with your advisor or other member of Customer,
              or other alerts from Customer. Any and all notifications/messages sent or received
              through the Application(s) are subject to these User Terms, including the mandatory
              arbitration provisions below. Civitas is not responsible and will not be held liable
              for delayed or undelivered notifications/messages. You can opt-out from receiving
              notifications/messages by changing your communication preferences on the Settings page
              within the Application(s).
            </p>
            <p>
              If you opt-in to receiving text messages through the Application(s), you are
              representing that you have the authority to agree to receive SMS messages on the
              mobile telephone number associated with Customer’s student information system (&quot;
              <strong>Your Mobile Number</strong>&quot;). If you have any questions about or wish to
              make a change to Your Mobile Number, please contact Customer. You agree to maintain
              accurate, complete and up-to-date information with Customer regarding Your Mobile
              Number, including that you agree to advise Customer immediately if you cease being the
              subscriber or regular user of Your Mobile Number. The frequency of the text messages
              you receive will depend upon your activity. Standard message and data rates may apply.
              Not all mobile devices may be supported.
            </p>
          </li>
          <li>
            <span>Mobile Application</span>
            <p>
              As part of the Customer Contract between Civitas and your educational institution, you
              may have access to mobile Applications developed by Civitas. These User Terms apply
              with equal force to any mobile Application(s). In order to use the mobile
              Application(s), you must have a compatible device to which the mobile Application(s)
              is downloaded (&quot;<strong>Device</strong>&quot;). By downloading the mobile
              Application(s), you confirm that you either own the Device or, if not owned by you,
              you have obtained permission from the owner to install and use the mobile
              Application(s) on such Device. You accept responsibility for any use of the mobile
              Application(s) whether or not the Device is owned by you.
            </p>
            <p>
              From time to time, updates to the mobile Application(s) may be made available on the
              app store where you downloaded the mobile Application(s) (&quot;
              <strong>App Store</strong>&quot;). Depending on the update, you may not be able to use
              the mobile Application(s) or all of the Applications’ features and functionalities
              until you update the mobile Application(s). Your submission of any information or
              Content on the mobile Application is governed by these User Terms, the Applicable Use
              Policy, and the Privacy Policy as well as any applicable App Store rules. You
              acknowledge that the availability of the mobile Application(s) may be dependent on the
              App Store from which you receive the mobile Application(s). You acknowledge that these
              User Terms are between you and Civitas and not with the App Store. The App Store is
              not responsible for the mobile Application(s), the Content, or any services provided
              in these User Terms or any claims relating thereto (e.g., product liability, legal
              compliance, or intellectual property infringement). You acknowledge that the App Store
              (and their respective affiliates) may be considered third party beneficiaries to these
              User Terms and may have the right to enforce these User Terms against you.
            </p>
          </li>
          <li>
            <span>Representations and Warranties</span>
            <p>You represent and warrant: </p>
            <ol type="i">
              <li>
                that you have read and understood these User Terms, the Authorized Use Policy, and
                the Privacy Policy;
              </li>
              <li>
                that you will not use the Application(s) or the Materials in a manner that violates
                these User Terms, including the Privacy Policy and the Authorized Use Policy as
                these terms and policies may be amended or modified from time to time;
              </li>
              <li>
                that you are solely responsible for your use of the Application(s) and/or the
                Materials, including, without limitation, the accuracy, appropriateness, and
                completeness of your Content as well as any use of your User Account, whether
                authorized by you or not;
              </li>
              <li>
                that you have the necessary rights and licenses, consents, permissions, waivers and
                releases to use and display your Content;
              </li>
              <li>
                that your Content (a) does not violate, misappropriate, or infringe any rights of
                Civitas or any third-party; (b) does not constitute defamation, invasion of privacy
                or publicity, or otherwise violate any rights or any third-party; or (c) is not
                designed for use in any illegal activity or promote illegal activities including,
                without limitation, in a manner that might be libelous or defamatory or otherwise
                malicious, illegal or harmful to any person or entity, or discriminatory based on
                race, sex, religion, nationality, disability, sexual orientation, or age; and
              </li>
              <li>
                that you will abide by all applicable local, state, national, foreign and
                international laws and regulations applicable to your access and use of the
                Application(s) and the Materials.
              </li>
            </ol>
          </li>
          <li>
            <span>Indemnification and Limitation of Liability</span>
            <p css={upperCss}>You expressly understand and agree that:</p>
            <p css={upperCss}>
              Your access to and use of THE APPLICATION(S) and the Materials is at your sole risk.
              THE APPLICATION(S) and the Materials are provided on an &quot;as is&quot; and &quot;as
              available&quot; basis WITH NO COMMITMENT TO ANY FUTURE FUNCTIONALITY OR FEATURES. YOU
              ACKNOWLEDGE you’re your ACCESS TO AND USE OF THE APPLICATION(S) IS AT YOUR OWN RISK.
              Except as otherwise expressly provided in these User Terms, Civitas, its parent,
              subsidiaries, and other affiliated companies and their respective officers, directors,
              employees, agents, and other representatives (collectively, the &quot;
              <strong>Civitas Parties</strong>&quot;), expressly disclaim all warranties of any
              kind, whether express or implied, including, but not limited to the implied warranties
              of merchantability, fitness for a particular purposes and non-INfringement. Without
              limiting the generality of the foregoing, the Civitas Parties make no warranty that:
              (i) THE APPLICATION(S) or the Materials will meet your requirements; (ii) THE
              APPLICATION(S) or the Materials will be uninterrupted, timely, secure, or error-free;
              (iii) information that may be obtained will be accurate or reliable; (iv) the quality
              of any products, services, information, or other material obtained by your through THE
              APPLICATION(S) or the Materials will meet your expectations or needs; and (v) except
              as in accordance with applicable law, any errors in THE APPLICATION(S) or the
              Materials will be corrected.
            </p>
            <p css={upperCss}>
              In no event will the Civitas Parties be liable for damages of any kind, under any
              legal theory, arising out of or in connection with your use, or in ability to use, THE
              APPLICATION(S) or the Materials, or any content thereon, including for any direct,
              indirect, incidental, special, exemplary or consequential damages of any type
              whatsoever whether based on breach of contract, breach of warranty, tort (including
              negligence), product liability or otherwise, even if an individual advises of the
              possibility of such damages, related to or arising from THE APPLICATION(S) or the
              Materials.
            </p>
            <p css={upperCss}>
              Notwithstanding the foregoing, the sole and maximum liability of the Civitas Parties
              for any reason, and your sole and exclusive remedy for any cause or claim whatsoever,
              shall be limited to (at civitas’ option) a pro-rata refund to customer of the pre-paid
              fees for your account covering the remainder of the term or TWENTY dollars ($20) in
              the aggregate. no action arising out of these user terms or your access to or use of
              THE APPLICATION(S) or the materials, regardless of form or the basis of the claim, may
              be brought by you more than one (1) year after the cause of action has arisen (or if
              multiple causes, from the date the first such cause arose). The foregoing does not
              affect any liability which cannot be excluded or limited under applicable law and does
              not limit either party’s right to seek and obtain equitable relief.
            </p>
            <p css={upperCss}>
              You agree to indemnify, hold harmless and defend the Civitas Parties, from and against
              any action, cause, claim, damage, debt, demand or liability, including reasonable
              costs and attorneys&apos; fees, asserted by any person, arising out of or relating to
              your use of or conduct on THE APPLICATION(S), any activity related to your User
              Account by you or any other person, any Content you provide to or transmit through THE
              APPLICATION(S), the breach of these User Terms including the warranties above, the
              Privacy Policy and the Acceptable Use Policy, or the unauthorized use of the
              application(s) or the Materials. We reserve the right to assume, at our sole expense,
              the exclusive defense and control of any such claim or action and all negotiation for
              settlement or compromise, and you agree to fully cooperate with us in the defense of
              any such claim, action, settlement or compromise negotiations, as requested by us.
            </p>
          </li>
          <li>
            <span>Arbitration</span>
            <p>
              PLEASE READ THIS SECTION CAREFULLY AS IT AFFECTS YOUR RIGHT TO FILE A LAWSUIT IN
              COURT.
            </p>
            <p>
              By agreeing to these User Terms, you also agree that any dispute, claim or controversy
              arising out of or related to the Application(s), the Materials, these User Terms, the
              Acceptable Use Policy, the Privacy Policy, or the breach, termination, enforcement,
              interpretation or validity thereof, or the use of the Application(s) (collectively
              &quot;Disputes&quot;) will be resolved solely through final and binding individual
              arbitration by a single arbitrator administered by the American Arbitration
              Association in accordance with its Commercial Arbitration Rules. This includes
              disputes arising out of or relating to interpretation or application of this
              Arbitration section, including its enforceability, revocability, or validity. Any such
              arbitration proceeding brought under this provision shall take place in Austin, Texas
              unless mutually agreed by the parties. Each party shall bear its own costs and
              expenses and an equal share of the arbitrator’s and administrative fees of
              arbitration. Judgment on an award rendered by the arbitrator may be entered in any
              court having jurisdiction.
            </p>
            <p>
              Notwithstanding the foregoing, either party may bring a lawsuit solely for injunctive
              relief to stop unauthorized use or abuse of the Application(s), or violation of any
              intellectual property. Such proceedings shall be brought in the federal or state
              courts in Austin, Texas.
            </p>
            <p>
              This provision shall not apply to users in countries that require agreements to be
              governed by the local laws of the user’s country.
            </p>
          </li>
          <li>
            <span>Applicable Law and Jurisdiction</span>
            <p>
              The Application(s) are tools intended for use by educational institutions and
              organizations and not for consumer purposes. To the maximum extent permitted by law,
              you hereby acknowledge and agree that consumer laws do not apply. If, however, any
              consumer laws do apply and cannot otherwise be excluded, nothing in these User Terms
              will restrict, exclude or modify any statutory warranties, guarantees, rights or
              remedies you have.
            </p>
            <p>
              These User Terms shall be governed by and interpreted in accordance with the laws of
              the State of Texas. The United States Arbitration Act shall govern the interpretation,
              enforcement, and proceedings pursuant to the arbitration clause above.
            </p>
          </li>
          <li>
            <span>Force Majeure</span>
            <p>
              Neither party will be liable for any failure or delay in its performance under these
              User Terms due to any cause beyond its reasonable control, including acts of war, acts
              of God, terrorism, earthquake, flood, embargo, riot, sabotage, pandemic, labor
              shortage or dispute, governmental act, or failure of the Internet (not resulting from
              the actions or omissions of Company), provided that the delayed party: (i) gives the
              other party prompt notice of such cause, and (ii) uses its reasonable commercial
              efforts to promptly correct such failure or delay in performance.
            </p>
          </li>
          <li>
            <span>Notices</span>
            <p>
              All notices under the User Terms will be by email. Notices to Civitas should be sent
              to <a href="mailto:legal@civitaslearning.com">legal@civitaslearning.com</a>. A notice
              will be deemed to have been duly given the day after it is sent. You agree that all
              notices we provide to you via email satisfy any legal requirement that such notices be
              in writing or be delivered in a particular manner. Notices under the Customer Contract
              will be delivered solely to Customer in accordance with the terms of that agreement.
            </p>
          </li>
          <li>
            <span>Assignment</span>
            <p>
              You may not assign any of your rights or delegate your obligations under these User
              Terms, including the Privacy Policy and the Acceptable Use Policy, whether by
              operation of law or otherwise, without the prior consent of Civitas. Civitas may
              assign these User Terms in their entirety (including all terms and conditions
              incorporated herein by reference), without your consent, to a corporate affiliate or
              in connection with a merger, acquisition, corporate reorganization, or sale of all or
              substantially all of the company’s assets.
            </p>
          </li>
          <li>
            <span>Miscellaneous</span>
            <p>
              These User Terms constitute the entire and only terms and conditions between Civitas
              and each user of the Application(s) or the Materials relating to the access and/or use
              of the Application(s) and the Materials. If any provision of these User Terms be
              deemed unlawful, void or for any reason unenforceable by a court of competent
              jurisdiction, the validity and enforceability of any remaining provisions will not be
              affected. No failure or delay by either party in exercising any right under the User
              Terms, including the Privacy Policy and the Acceptable Use Policy, will constitute a
              waiver of that right. No waiver under the User Terms will be effective unless made in
              writing and signed by an authorized representative of the party being deemed to have
              granted the waiver. Other than the Civitas Parties, no other person or company will be
              a third-party beneficiary to these User Terms.
            </p>
          </li>
        </ol>
        <p>
          If you have any comments or questions regarding these User Terms, or wish to report any
          violation of these User Terms or applicable law, you may reach us at{' '}
          <a href="mailto:legal@civitaslearning.com">legal@civitaslearning.com</a>.
        </p>
      </div>
    </ModalBody>
    <ModalFooter>
      <SecondaryButton onClick={onCancel}>Ok</SecondaryButton>
    </ModalFooter>
  </Modal>
)

TermsOfServiceModal.defaultProps = defaultProps

export default TermsOfServiceModal
